import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, User, Search, UserPlus, Mail, Phone, Building, X, Check, FileText } from 'lucide-react';
import { useClients, Client } from '../context/ClientsContext';
import Card from '../components/UI/Card';
import Button from '../components/UI/Button';
import Input from '../components/UI/Input';

const Clients: React.FC = () => {
  const { clients, addClient } = useClients();
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    company: '',
    siret: '',
    vat_number: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const navigate = useNavigate();
  
  const filteredClients = clients.filter((client) => 
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewClient(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };
  
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!newClient.name.trim()) {
      newErrors.name = 'Le nom du contact est requis';
    }
    
    if (newClient.email && !/\S+@\S+\.\S+/.test(newClient.email)) {
      newErrors.email = "L'email n'est pas valide";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    setTimeout(() => {
      addClient(newClient);
      setNewClient({
        name: '',
        email: '',
        phone: '',
        address: '',
        company: '',
        siret: '',
        vat_number: '',
      });
      setShowAddForm(false);
      setIsSubmitting(false);
    }, 500);
  };
  
  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Clients</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Gérez vos clients pour les factures et devis.
          </p>
        </div>
        
        <Button 
          variant="primary"
          leftIcon={<Plus className="h-4 w-4" />}
          onClick={() => setShowAddForm(true)}
        >
          Ajouter un Client
        </Button>
      </div>
      
      <div className="flex items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 dark:text-gray-400" />
          <Input
            placeholder="Rechercher des clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>
      
      {showAddForm && (
        <Card>
          <Card.Header 
            title="Ajouter un Nouveau Client"
            action={
              <button 
                onClick={() => setShowAddForm(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            }
          />
          
          <form onSubmit={handleSubmit}>
            <Card.Content className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Nom du contact"
                  name="name"
                  value={newClient.name}
                  onChange={handleInputChange}
                  placeholder="Jean Dupont"
                  required
                  error={errors.name}
                  leftIcon={<User className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                />
                
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={newClient.email}
                  onChange={handleInputChange}
                  placeholder="jean@exemple.fr"
                  error={errors.email}
                  leftIcon={<Mail className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="Téléphone"
                  name="phone"
                  value={newClient.phone}
                  onChange={handleInputChange}
                  placeholder="06 12 34 56 78"
                  leftIcon={<Phone className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                />
                
                <Input
                  label="Entreprise"
                  name="company"
                  value={newClient.company}
                  onChange={handleInputChange}
                  placeholder="Entreprise SARL"
                  leftIcon={<Building className="h-4 w-4 text-gray-500 dark:text-gray-400" />}
                />
              </div>
              
              <Input
                label="Adresse"
                name="address"
                value={newClient.address}
                onChange={handleInputChange}
                placeholder="123 Rue de Paris, 75001 Paris"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input
                  label="N° de SIRET"
                  name="siret"
                  value={newClient.siret}
                  onChange={handleInputChange}
                  placeholder="123 456 789 00012"
                />
                
                <Input
                  label="N° de TVA"
                  name="vat_number"
                  value={newClient.vat_number}
                  onChange={handleInputChange}
                  placeholder="FR12345678900"
                />
              </div>
            </Card.Content>
            
            <Card.Footer className="flex justify-end space-x-3">
              <Button 
                variant="outline" 
                type="button" 
                onClick={() => setShowAddForm(false)}
              >
                Annuler
              </Button>
              <Button 
                type="submit" 
                isLoading={isSubmitting}
                leftIcon={<Check className="h-4 w-4" />}
              >
                {isSubmitting ? 'Ajout en cours...' : 'Ajouter le Client'}
              </Button>
            </Card.Footer>
          </form>
        </Card>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {filteredClients.map((client: Client) => (
          <Card 
            key={client.id} 
            interactive 
            onClick={() => navigate(`/app/clients/${client.id}`)}
            className="animate-slide-up"
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center text-primary-700 dark:text-primary-300">
                  <User className="h-5 w-5" />
                </div>
              </div>
              <div className="ml-3 flex-1 overflow-hidden">
                <h3 className="text-lg font-semibold truncate">{client.name}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  {client.company || 'Particulier'}
                </p>
                {client.email && (
                  <div className="mt-2 flex items-center">
                    <Mail className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    <span className="ml-1 text-sm text-gray-600 dark:text-gray-400 truncate">
                      {client.email}
                    </span>
                  </div>
                )}
                {client.phone && (
                  <div className="mt-1 flex items-center">
                    <Phone className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                    <span className="ml-1 text-sm text-gray-600 dark:text-gray-400 truncate">
                      {client.phone}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
        
        {filteredClients.length === 0 && (
          <div className="col-span-full py-12 flex flex-col items-center justify-center text-center">
            <div className="h-16 w-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-gray-500 dark:text-gray-400 mb-4">
              <UserPlus className="h-8 w-8" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Aucun client trouvé</h3>
            <p className="mt-1 text-gray-500 dark:text-gray-400">
              {searchTerm 
                ? `Aucun résultat pour "${searchTerm}". Essayez un autre terme de recherche.` 
                : "Vous n'avez pas encore ajouté de clients."}
            </p>
            {!searchTerm && (
              <Button 
                variant="outline" 
                className="mt-4"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setShowAddForm(true)}
              >
                Ajouter Votre Premier Client
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Clients;